import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Home, Building2, Users, Key, Clock, Palmtree, Globe, DollarSign, CheckCircle } from 'lucide-react';

const DemoPage = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('landlord');

  const demos = {
    landlord: {
      title: 'Landlord Portal',
      icon: Home,
      color: 'blue',
      description: 'Comprehensive property and tenant management for landlords',
      features: [
        'Dashboard with revenue analytics and property overview',
        'Tenant management with invitation codes',
        'Property and unit management with rent pricing',
        'Payout requests to your bank account',
        'Payment tracking and history',
        'Maintenance request monitoring'
      ],
      screenshots: [
        {
          title: 'Dashboard Overview',
          caption: 'See total tenants, properties, revenue collected, and outstanding dues at a glance. Recent payments and maintenance requests displayed prominently.',
          imageUrl: '/demo/landlord-dashboard.png'
        },
        {
          title: 'Tenant Management',
          caption: 'Generate invitation codes for new tenants, track registration status, and manage tenant accounts. Copy invitation links with one click.',
          imageUrl: '/demo/landlord-tenants.png'
        },
        {
          title: 'Property & Units',
          caption: 'Manage all your properties and units. Set individual rent prices for each unit. View occupancy status and unit details in organized grids.',
          imageUrl: '/demo/landlord-properties.png'
        },
        {
          title: 'Payment Tracking',
          caption: 'Track all tenant payments with detailed history. View payment methods, amounts, and status. Monitor outstanding balances and late payments.',
          imageUrl: '/demo/landlord-payments.png'
        }
      ]
    },
    hoa_admin: {
      title: 'HOA Admin Portal',
      icon: Building2,
      color: 'green',
      description: 'Complete HOA community management system',
      features: [
        'Dashboard with buildings, units, and revenue metrics',
        'Owner directory with detailed payment tracking',
        'Financial summary with income and expenses',
        'Maintenance request management',
        'Vendor management system',
        'Dues configuration and late fee settings'
      ],
      screenshots: [
        {
          title: 'HOA Dashboard',
          caption: 'Track total buildings, units, revenue, and occupancy rates. Monitor monthly revenue trends and pending dues. View compliance metrics.',
          imageUrl: '/demo/hoa-dashboard.png'
        },
        {
          title: 'Owner Management',
          caption: 'Complete owner directory with payment status tracking. Expandable payment history shows month-by-month dues payments. Filter by paid/unpaid/overdue.',
          imageUrl: '/demo/hoa-owners.png'
        },
        {
          title: 'Financial Summary',
          caption: 'Detailed income breakdown by category. Track expenses (maintenance, utilities, admin, reserves). Month-over-month comparison and net income calculation.',
          imageUrl: '/demo/hoa-financial.png'
        },
        {
          title: 'Maintenance Requests',
          caption: 'View and manage all maintenance requests from owners. Schedule new maintenance, assign to vendors, and track completion status with costs.',
          imageUrl: '/demo/hoa-maintenance.png'
        }
      ]
    },
    hoa_owner: {
      title: 'HOA Owner Portal',
      icon: Key,
      color: 'purple',
      description: 'Simple and convenient portal for HOA homeowners',
      features: [
        'Property details and dues information',
        'One-click dues payment',
        'Detailed payment history with receipts',
        'Maintenance request submission',
        'Account status tracking',
        'HOA community documents access'
      ],
      screenshots: [
        {
          title: 'Owner Dashboard',
          caption: 'View your property details, current dues amount, and payment due date. Quick access to pay dues and submit maintenance requests.',
          imageUrl: '/demo/owner-dashboard.png'
        },
        {
          title: 'Payment History',
          caption: 'Complete payment history with dates, amounts, status, and payment methods. Download receipts and track outstanding balances.',
          imageUrl: '/demo/owner-payments.png'
        },
        {
          title: 'Pay Dues',
          caption: 'Simple, secure payment process. Enter payment amount, choose payment method, and complete transaction in seconds.',
          imageUrl: '/demo/owner-pay.png'
        }
      ]
    },
    tenant: {
      title: 'Tenant Portal',
      icon: Users,
      color: 'orange',
      description: 'Easy-to-use portal for tenants',
      features: [
        'Property information and lease details',
        'Pay rent securely online',
        'Payment history and receipts',
        'Submit maintenance requests with photos',
        'Track maintenance request status',
        'Direct communication with landlord'
      ],
      screenshots: [
        {
          title: 'Tenant Dashboard',
          caption: 'View your property information, rent amount, and due date. Quick access to pay rent and submit maintenance requests.',
          imageUrl: '/demo/tenant-dashboard.png'
        },
        {
          title: 'Pay Rent',
          caption: 'Secure online rent payment via credit card or bank transfer. View payment confirmation immediately.',
          imageUrl: '/demo/tenant-pay.png'
        },
        {
          title: 'Maintenance Requests',
          caption: 'Submit maintenance requests with descriptions and photos. Track status from submitted to completed. Communication thread with property manager.',
          imageUrl: '/demo/tenant-maintenance.png'
        }
      ]
    },
    property_manager: {
      title: 'Property Manager Portal',
      icon: Building2,
      color: 'purple',
      description: 'Complete white-label solution for property management companies',
      features: [
        'Consolidated dashboard across all properties',
        'Full tenant management with payment tracking',
        'Maintenance request system with vendor assignment',
        'Financial reports and owner statements',
        'White-label branding customization',
        'Vacation rentals integration'
      ],
      screenshots: [
        {
          title: 'Property Manager Dashboard',
          caption: 'Comprehensive overview of all managed properties with key metrics: total properties, units, tenants, occupancy rate, revenue, and maintenance requests at a glance.',
          imageUrl: '/demo/pm-dashboard.png'
        },
        {
          title: 'Tenant Management',
          caption: 'Complete tenant directory with contact info, property assignment, rent amounts, payment status, and balance tracking. Filter by status and search functionality.',
          imageUrl: '/demo/pm-tenants.png'
        },
        {
          title: 'Maintenance Tracking',
          caption: 'Track all maintenance requests across properties. Priority levels (urgent, high, medium, low), status tracking, vendor assignment, and cost monitoring.',
          imageUrl: '/demo/pm-maintenance.png'
        },
        {
          title: 'White-Label Settings',
          caption: 'Customize the platform with your company branding: logo, colors, custom domain, and remove Estate Flow branding for your clients.',
          imageUrl: '/demo/pm-whitelabel.png'
        }
      ]
    },
    vacation_rentals: {
      title: 'Vacation Rentals',
      icon: Home,
      color: 'emerald',
      description: 'Short-term rental management with OTA channel sync and Book Direct widget',
      features: [
        'Channel sync: Expedia, Booking.com, VRBO, Airbnb',
        'Book Direct & Save widget for your website',
        'Dynamic pricing engine (seasonal, weekend, last-minute)',
        'Availability calendar management',
        'Only 2% platform fee vs 15-20% OTA commission',
        'Multi-currency support (USD, CAD, EUR & more)'
      ],
      screenshots: [
        {
          title: 'Vacation Rentals Dashboard',
          caption: 'Overview of all vacation properties with bookings, revenue, and channel performance. Track direct bookings vs OTA bookings and measure savings.',
          imageUrl: '/demo/vr-dashboard.png'
        },
        {
          title: 'Channel Manager',
          caption: 'Connect your properties to major OTAs: Expedia, Booking.com, VRBO, and Airbnb. Automatic availability sync and rate management.',
          imageUrl: '/demo/vr-channels.png'
        },
        {
          title: 'Book Direct Widget',
          caption: 'Embeddable booking widget for your website. Guests save 10-15% when booking direct, you pay only 2% platform fee instead of 15-20% OTA commission.',
          imageUrl: '/demo/vr-widget.png'
        },
        {
          title: 'Dynamic Pricing',
          caption: 'Automated pricing rules: weekend premiums, seasonal adjustments, and last-minute deals. Maximize revenue based on demand.',
          imageUrl: '/demo/vr-pricing.png'
        }
      ]
    }
  };

  const currentDemo = demos[selectedRole];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Estate Flow Demo</h1>
                <p className="text-sm text-gray-600">Explore our different user portals</p>
              </div>
            </div>
            <Button onClick={() => navigate('/onboarding')} className="bg-emerald-600 hover:bg-emerald-700">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Role Selector */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {Object.entries(demos).map(([key, demo]) => {
            const Icon = demo.icon;
            const isActive = selectedRole === key;
            const colors = {
              blue: 'border-blue-500 bg-blue-50',
              green: 'border-green-500 bg-green-50',
              purple: 'border-purple-500 bg-purple-50',
              orange: 'border-orange-500 bg-orange-50',
              gray: 'border-gray-300 bg-gray-50',
              emerald: 'border-emerald-500 bg-emerald-50'
            };
            
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-all ${isActive ? `border-2 ${colors[demo.color]}` : 'border hover:border-gray-400'}`}
                onClick={() => setSelectedRole(key)}
              >
                <CardContent className="p-4 text-center">
                  <Icon className={`h-8 w-8 mx-auto mb-2 ${isActive ? `text-${demo.color}-600` : 'text-gray-600'}`} />
                  <p className={`text-sm font-medium ${isActive ? `text-${demo.color}-900` : 'text-gray-900'}`}>
                    {demo.title}
                  </p>
                  {demo.comingSoon && (
                    <Badge className="mt-2 bg-gray-200 text-gray-700 text-xs">Coming Soon</Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Demo Content */}
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-start space-x-4">
              <div className={`p-4 rounded-lg bg-${currentDemo.color}-100`}>
                <currentDemo.icon className={`h-10 w-10 text-${currentDemo.color}-600`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-3xl font-bold text-gray-900">{currentDemo.title}</h2>
                  {currentDemo.comingSoon && (
                    <Badge className="bg-yellow-100 text-yellow-800">Coming Soon</Badge>
                  )}
                </div>
                <p className="text-lg text-gray-600 mb-6">{currentDemo.description}</p>
                
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-900">Key Features:</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {currentDemo.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <span className="text-emerald-600 mt-1">✓</span>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Screenshots */}
          <div className="space-y-8">
            {currentDemo.screenshots.map((screenshot, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6 border-b bg-gray-50">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{screenshot.title}</h3>
                  <p className="text-gray-600">{screenshot.caption}</p>
                </div>
                <div className="p-8 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center min-h-[400px]">
                  {currentDemo.comingSoon ? (
                    <div className="text-center">
                      <Clock className="h-24 w-24 mx-auto text-gray-400 mb-4" />
                      <h4 className="text-2xl font-bold text-gray-700 mb-2">Coming Soon</h4>
                      <p className="text-gray-600 max-w-md">
                        We're working hard to bring you the Property Manager portal with advanced features for managing multiple properties.
                      </p>
                    </div>
                  ) : (
                    <div className="w-full">
                      <img 
                        src={screenshot.imageUrl} 
                        alt={screenshot.title}
                        className="w-full rounded-lg shadow-2xl border-4 border-white"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="600"%3E%3Crect fill="%23f0f0f0" width="1200" height="600"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%23666"%3EImage not available%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          {!currentDemo.comingSoon && (
            <div className="bg-emerald-600 rounded-lg shadow-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to get started with {currentDemo.title}?
              </h3>
              <p className="text-emerald-100 mb-6">
                Professional property management made simple.
              </p>
              <Button 
                onClick={() => navigate('/onboarding')} 
                size="lg"
                className="bg-white text-emerald-600 hover:bg-gray-100"
              >
                Get Started →
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
